import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from './stores/authStore';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { FEATURE_PERMISSION_BY_PATH, canOpenFeature } from './config/featurePermissions';
import { getWeComEntryPath, isMobileDevice } from './utils/device';
import { autoReloadForChunkError } from './utils/chunkLoadRecovery';
import WeComConversationInheritSidebarPage from './pages/WeComConversationInheritSidebarPage';

const lazyWithTimeout = <T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  timeoutMs = 20_000,
) => lazy(() => new Promise<{ default: T }>((resolve, reject) => {
  const timer = window.setTimeout(() => {
    reject(new Error('Loading chunk timed out. Please refresh and try again.'));
  }, timeoutMs);
  loader().then(resolve, reject).finally(() => window.clearTimeout(timer));
}).catch((error) => {
  if (autoReloadForChunkError(error)) {
    // Keep Suspense mounted while the browser performs the recovery reload.
    return new Promise<{ default: T }>(() => {});
  }
  throw error;
}));

// 非核心页面保留按需加载，但必须有超时兜底，避免企微 WebView 永久停在转圈页。
const AppLayout = lazyWithTimeout(() => import('./components/layout/AppLayout').then(m => ({ default: m.AppLayout })));
const WeComOpenDataRuntime = lazyWithTimeout(() => import('./components/common/WeComOpenDataRuntime'));
const LoginPage = lazyWithTimeout(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const HomeOverviewPage = lazyWithTimeout(() => import('./pages/HomeOverviewPage'));
const DashboardPage = lazyWithTimeout(() => import('./pages/DashboardPage').then(m => ({ default: m.default || m.DashboardPage })));
// const ConversationListPage = lazy(() => import('./pages/ConversationListPage'));
const ConversationListPage = lazyWithTimeout(() => import('./pages/ConversationListPage'));
const ConversationDetailPage = lazyWithTimeout(() => import('./pages/ConversationDetailPage'));
const ChatArchivePage = lazyWithTimeout(() => import('./pages/ChatArchivePage'));
const RiskAlertPage = lazyWithTimeout(() => import('./pages/RiskAlertPage'));
const RiskDataPage = lazyWithTimeout(() => import('./pages/RiskDataPage'));
const RiskRecordsPage = lazyWithTimeout(() => import('./pages/RiskRecordsPage'));
const ReportPage = lazyWithTimeout(() => import('./pages/ReportPage'));
const ContactCustomerDetailPage = lazyWithTimeout(() => import('./pages/ContactCustomerDetailPage'));
const OperationLogsPage = lazyWithTimeout(() => import('./pages/OperationLogsPage'));
const PermissionPage = lazyWithTimeout(() => import('./pages/PermissionPage'));
const AccountsPage = lazyWithTimeout(() => import('./pages/AccountsPage'));
const DepartmentManagementPage = lazyWithTimeout(() => import('./pages/DepartmentManagementPage'));
const AgentManagementPage = lazyWithTimeout(() => import('./pages/AgentManagementPage'));
const RuleConfigPage = lazyWithTimeout(() => import('./pages/RuleConfigPage'));
const ConversationInheritPage = lazyWithTimeout(() => import('./pages/ConversationInheritPage'));
const InheritHistoryPage = lazyWithTimeout(() => import('./pages/InheritHistoryPage'));
const ConversationExportPage = lazyWithTimeout(() => import('./pages/ConversationExportPage'));
const FileRecordingPage = lazyWithTimeout(() => import('./pages/FileRecordingPage'));
const TimeoutReminderPage = lazyWithTimeout(() => import('./pages/TimeoutReminderPage'));
const TimeoutDataPage = lazyWithTimeout(() => import('./pages/TimeoutDataPage'));
const WeComReplyPage = lazyWithTimeout(() => import('./pages/WeComReplyPage'));
const MobileLayout = lazyWithTimeout(() => import('./pages/mobile/MobileLayout'));
const MobileDashboardPage = lazyWithTimeout(() => import('./pages/mobile/MobileDashboardPage'));
const MobileConversationsPage = lazyWithTimeout(() => import('./pages/mobile/MobileConversationsPage'));
const MobileConversationDetailPage = lazyWithTimeout(() => import('./pages/mobile/MobileConversationDetailPage'));
const MobileAlertsPage = lazyWithTimeout(() => import('./pages/mobile/MobileAlertsPage'));
const MobileAlertDetailPage = lazyWithTimeout(() => import('./pages/mobile/MobileAlertDetailPage'));
const MobileMePage = lazyWithTimeout(() => import('./pages/mobile/MobileMePage'));
const MobileStatisticsPage = lazyWithTimeout(() => import('./pages/mobile/MobileStatisticsPage'));
const MobileManagementPage = lazyWithTimeout(() => import('./pages/mobile/MobileManagementPage'));
const MobileEnterprisePage = lazyWithTimeout(() => import('./pages/mobile/MobileEnterprisePage'));
const MobileRiskRecordsPage = lazyWithTimeout(() => import('./pages/mobile/MobileRiskRecordsPage'));
const MobileReportDetailPage = lazyWithTimeout(() => import('./pages/mobile/MobileReportDetailPage'));
const MobileTimeoutStatisticsPage = lazyWithTimeout(() => import('./pages/mobile/MobileTimeoutStatisticsPage'));
const MobileOperationLogsPage = lazyWithTimeout(() => import('./pages/mobile/MobileOperationLogsPage'));
const MobileResponsiveFeaturePage = lazyWithTimeout(() => import('./pages/mobile/MobileResponsiveFeaturePage'));

const MobileAwareIndexRoute: React.FC = () => (
  isMobileDevice() ? <Navigate to="/m" replace /> : <Navigate to="/home" replace />
);

const MobileAwareFeatureRoute: React.FC<{ mobileTo: string; children: React.ReactNode; preserveSearch?: boolean }> = ({ mobileTo, children, preserveSearch = true }) => {
  const location = useLocation();
  const target = preserveSearch && location.search ? `${mobileTo}${location.search}` : mobileTo;
  return isMobileDevice() ? <Navigate to={target} replace /> : <>{children}</>;
};

const MobileConversationPcRedirect: React.FC = () => {
  const location = useLocation();
  const conversationId = location.pathname.split('/').filter(Boolean)[1];
  if (isMobileDevice() && conversationId) {
    return <Navigate to={`/m/conversations/${conversationId}`} replace />;
  }
  return <ConversationDetailPage />;
};

// 加载中组件
const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress color="primary" />
  </Box>
);

// 路由守卫组件
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredFeaturePath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermission, requiredFeaturePath }) => {
  const { isAuthenticated, hasPermission, user, token, loadUser } = useAuthStore();
  const location = useLocation();
  const hasAuthToken = Boolean(token || (typeof window !== 'undefined' && (window as any).__wecom_token__));

  useEffect(() => {
    if (hasAuthToken && !user) {
      loadUser().catch((error) => {
        console.warn('[ProtectedRoute] Failed to restore current user:', error);
      });
    }
  }, [hasAuthToken, loadUser, user]);

  if (!isAuthenticated && !hasAuthToken) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if ((!isAuthenticated && hasAuthToken) || (requiredFeaturePath && !user)) {
    return <LoadingFallback />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/403" replace />;
  }

  if (
    requiredFeaturePath &&
    !canOpenFeature(user, FEATURE_PERMISSION_BY_PATH[requiredFeaturePath])
  ) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};

/** 聊天工具栏令牌已可用时直接进入，避免全局登录态恢复重复显示加载态。 */
const WeComToolbarRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuthStore();
  const location = useLocation();
  const hasAuthToken = Boolean(token || (typeof window !== 'undefined' && (window as any).__wecom_token__));

  if (!isAuthenticated && !hasAuthToken) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return <>{children}</>;
};

// 公共路由组件（已登录则跳转到首页）
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const hasRedirectToken = (
    new URLSearchParams(window.location.search).has('token')
    || new URLSearchParams(window.location.hash.replace(/^#/, '')).has('token')
  );
  if (hasRedirectToken) {
    return <>{children}</>;
  }
  return isAuthenticated ? <Navigate to={getWeComEntryPath()} replace /> : <>{children}</>;
};

const App: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const shouldLoadWeComRuntime = Boolean(token || (window as any).__wecom_token__);
  return (
    <Suspense fallback={<LoadingFallback />}>
      {shouldLoadWeComRuntime && (
        <Suspense fallback={null}>
          <WeComOpenDataRuntime />
        </Suspense>
      )}
      <Routes>
        {/* 公共路由 */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        <Route
          path="/wecom/reply/:conversationId"
          element={<WeComReplyPage />}
        />

        <Route
          path="/message"
          element={
            <WeComToolbarRoute>
              <WeComConversationInheritSidebarPage />
            </WeComToolbarRoute>
          }
        />

        {/* 移动端 H5 路由 */}
        <Route
          path="/m"
          element={
            <ProtectedRoute>
              <MobileLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MobileDashboardPage />} />
          <Route path="conversations" element={<ProtectedRoute requiredFeaturePath="/conversations"><MobileConversationsPage /></ProtectedRoute>} />
          <Route path="conversations/:conversationId" element={<ProtectedRoute requiredFeaturePath="/conversations"><MobileConversationDetailPage /></ProtectedRoute>} />
          <Route path="alerts" element={<ProtectedRoute requiredFeaturePath="/alerts"><MobileAlertsPage /></ProtectedRoute>} />
          <Route path="alerts/:alertId" element={<ProtectedRoute requiredFeaturePath="/alerts"><MobileAlertDetailPage /></ProtectedRoute>} />
          <Route path="statistics" element={<ProtectedRoute requiredFeaturePath="/reports"><MobileStatisticsPage /></ProtectedRoute>} />
          <Route path="statistics/contact" element={<ProtectedRoute requiredFeaturePath="/reports/contact-customer-detail"><MobileReportDetailPage mode="contact" /></ProtectedRoute>} />
          <Route path="statistics/group" element={<ProtectedRoute requiredFeaturePath="/reports/group-chat-detail"><MobileReportDetailPage mode="group" /></ProtectedRoute>} />
          <Route path="statistics/timeout" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/data"><MobileTimeoutStatisticsPage /></ProtectedRoute>} />
          <Route path="risk-records" element={<ProtectedRoute requiredFeaturePath="/risk-records"><MobileRiskRecordsPage /></ProtectedRoute>} />
          <Route path="customer-loss" element={<Navigate to="/m/risk-records?tab=customer_loss" replace />} />
          <Route path="archive-rejections" element={<Navigate to="/m/risk-records?tab=archive_rejections" replace />} />
          <Route path="inherit" element={<ProtectedRoute requiredFeaturePath="/conversation-inherit"><MobileResponsiveFeaturePage title="会话继承" backTo="/m"><ConversationInheritPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="inherit/history" element={<ProtectedRoute requiredFeaturePath="/conversation-inherit/history"><MobileResponsiveFeaturePage title="历史继承" backTo="/m"><InheritHistoryPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings" element={<MobileManagementPage />} />
          <Route path="settings/enterprise" element={<MobileEnterprisePage />} />
          <Route path="settings/logs" element={<ProtectedRoute requiredFeaturePath="/settings/logs"><MobileOperationLogsPage /></ProtectedRoute>} />
          <Route path="settings/permissions" element={<ProtectedRoute requiredFeaturePath="/settings/permissions"><MobileResponsiveFeaturePage title="权限管理"><PermissionPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings/accounts" element={<ProtectedRoute requiredFeaturePath="/settings/accounts"><MobileResponsiveFeaturePage title="账号管理"><AccountsPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings/departments" element={<ProtectedRoute requiredFeaturePath="/departments"><MobileResponsiveFeaturePage title="企业组织"><DepartmentManagementPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings/agents" element={<ProtectedRoute requiredFeaturePath="/agents"><MobileResponsiveFeaturePage title="应用管理"><AgentManagementPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings/rules" element={<ProtectedRoute requiredFeaturePath="/rules"><MobileResponsiveFeaturePage title="规则与知识集"><ErrorBoundary><RuleConfigPage /></ErrorBoundary></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="settings/timeout" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/settings"><MobileResponsiveFeaturePage title="超时提醒设置"><TimeoutReminderPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="tools/conversation-export" element={<ProtectedRoute requiredFeaturePath="/conversation-export"><MobileResponsiveFeaturePage title="会话导出" backTo="/m"><ConversationExportPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="tools/file-recording" element={<ProtectedRoute requiredFeaturePath="/file-recording"><MobileResponsiveFeaturePage title="文件录音" backTo="/m"><FileRecordingPage /></MobileResponsiveFeaturePage></ProtectedRoute>} />
          <Route path="me" element={<MobileMePage />} />
        </Route>

        {/* 受保护的路由 */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<MobileAwareIndexRoute />} />
          <Route path="home" element={<HomeOverviewPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="conversations" element={<ProtectedRoute requiredFeaturePath="/conversations"><MobileAwareFeatureRoute mobileTo="/m/conversations"><ChatArchivePage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="conversations/:conversationId" element={<ProtectedRoute requiredFeaturePath="/conversations"><MobileConversationPcRedirect /></ProtectedRoute>} />
          <Route path="alerts" element={<ProtectedRoute requiredFeaturePath="/alerts"><MobileAwareFeatureRoute mobileTo="/m/alerts"><RiskDataPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="risk-records" element={<ProtectedRoute requiredFeaturePath="/risk-records"><MobileAwareFeatureRoute mobileTo="/m/risk-records"><RiskRecordsPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="customer-loss" element={<Navigate to="/risk-records?tab=customer_loss" replace />} />
          <Route path="archive-rejections" element={<Navigate to="/risk-records?tab=archive_rejections" replace />} />
          <Route path="reports" element={<ProtectedRoute requiredFeaturePath="/reports"><MobileAwareFeatureRoute mobileTo="/m/statistics"><Navigate to="/reports/employee-ranking" replace /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="reports/contact-customer-detail" element={<ProtectedRoute requiredFeaturePath="/reports/contact-customer-detail"><MobileAwareFeatureRoute mobileTo="/m/statistics/contact"><ContactCustomerDetailPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="reports/group-chat-detail" element={<ProtectedRoute requiredFeaturePath="/reports/group-chat-detail"><MobileAwareFeatureRoute mobileTo="/m/statistics/group"><ContactCustomerDetailPage mode="group" /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="reports/employee-ranking" element={<ProtectedRoute requiredFeaturePath="/reports/employee-ranking"><MobileAwareFeatureRoute mobileTo="/m/statistics"><ReportPage mode="employee" /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="reports/department-ranking" element={<ProtectedRoute requiredFeaturePath="/reports/department-ranking"><MobileAwareFeatureRoute mobileTo="/m/statistics"><ReportPage mode="department" /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="settings" element={isMobileDevice() ? <Navigate to="/m/settings" replace /> : <Navigate to="/settings/permissions" replace />} />
          <Route path="settings/logs" element={<ProtectedRoute requiredFeaturePath="/settings/logs"><MobileAwareFeatureRoute mobileTo="/m/settings/logs"><OperationLogsPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="settings/permissions" element={<ProtectedRoute requiredFeaturePath="/settings/permissions"><MobileAwareFeatureRoute mobileTo="/m/settings/permissions"><PermissionPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="settings/accounts" element={<ProtectedRoute requiredFeaturePath="/settings/accounts"><MobileAwareFeatureRoute mobileTo="/m/settings/accounts"><AccountsPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="settings/timeout-reminder" element={<ProtectedRoute requiredFeaturePath="/settings/timeout-reminder"><MobileAwareFeatureRoute mobileTo="/m/settings/timeout"><TimeoutReminderPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder" element={isMobileDevice() ? <Navigate to="/m/settings/timeout" replace /> : <Navigate to="/timeout-reminder/settings" replace />} />
          <Route path="timeout-reminder/settings" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/settings"><MobileAwareFeatureRoute mobileTo="/m/settings/timeout"><TimeoutReminderPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder/records" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/records"><MobileAwareFeatureRoute mobileTo="/m/alerts?risk_type=timeout" preserveSearch={false}><RiskAlertPage title="超时记录" fixedRiskType="timeout" /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder/data" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/data"><MobileAwareFeatureRoute mobileTo="/m/statistics/timeout"><Navigate to="/timeout-reminder/data/detail" replace /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder/data/detail" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/data/detail"><MobileAwareFeatureRoute mobileTo="/m/statistics/timeout?view=detail" preserveSearch={false}><TimeoutDataPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder/data/single" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/data/single"><MobileAwareFeatureRoute mobileTo="/m/statistics/timeout?view=single" preserveSearch={false}><TimeoutDataPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="timeout-reminder/data/group" element={<ProtectedRoute requiredFeaturePath="/timeout-reminder/data/group"><MobileAwareFeatureRoute mobileTo="/m/statistics/timeout?view=group" preserveSearch={false}><TimeoutDataPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="conversation-inherit" element={<ProtectedRoute requiredFeaturePath="/conversation-inherit"><MobileAwareFeatureRoute mobileTo="/m/inherit"><ConversationInheritPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="conversation-inherit/history" element={<ProtectedRoute requiredFeaturePath="/conversation-inherit/history"><MobileAwareFeatureRoute mobileTo="/m/inherit/history"><InheritHistoryPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="conversation-export" element={<ProtectedRoute requiredFeaturePath="/conversation-export"><MobileAwareFeatureRoute mobileTo="/m/tools/conversation-export"><ConversationExportPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="file-recording" element={<ProtectedRoute requiredFeaturePath="/file-recording"><MobileAwareFeatureRoute mobileTo="/m/tools/file-recording"><FileRecordingPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="departments" element={<ProtectedRoute requiredFeaturePath="/departments"><MobileAwareFeatureRoute mobileTo="/m/settings/departments"><DepartmentManagementPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route path="agents" element={<ProtectedRoute requiredFeaturePath="/agents"><MobileAwareFeatureRoute mobileTo="/m/settings/agents"><AgentManagementPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
          <Route
            path="rules"
            element={
              <ProtectedRoute requiredFeaturePath="/rules">
                <ErrorBoundary>
                  <MobileAwareFeatureRoute mobileTo="/m/settings/rules"><RuleConfigPage /></MobileAwareFeatureRoute>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="conversation-list" element={<ProtectedRoute requiredFeaturePath="/conversations"><MobileAwareFeatureRoute mobileTo="/m/conversations"><ConversationListPage /></MobileAwareFeatureRoute></ProtectedRoute>} />
        </Route>

        {/* 404 页面 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;
